import React, { useState } from 'react';
import { ResultCard } from '../components/shared/ResultCard';
import { 
  Play, Pause, CheckCircle2, Clock, AlertTriangle, Users, 
  ChevronRight, ChevronDown, RotateCcw, Shield, Calendar,
  ArrowRight, FileText, Eye
} from 'lucide-react';
import { workflowTemplates, workflowRuns } from '../data/automationData';
import { useToast } from '../context/ToastContext';

const statusColors = {
  completed: { bg: 'bg-primary/10', text: 'text-primary', icon: CheckCircle2 },
  in_progress: { bg: 'bg-primary/10', text: 'text-primary', icon: Play },
  pending_approval: { bg: 'bg-slate-200', text: 'text-slate-700', icon: Clock },
  failed: { bg: 'bg-slate-900', text: 'text-white', icon: AlertTriangle }
};

export const Automation = () => {
  const { addToast } = useToast();
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedRun, setSelectedRun] = useState(null);
  const [expandedWorkflow, setExpandedWorkflow] = useState(null);

  const handleAction = (action) => {
    addToast(`Demo action: ${action}`, 'info');
  };

  const renderWorkflowDetail = (template) => (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-xl shadow-lg border border-gray-200 w-full max-w-3xl max-h-[90vh] overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold">{template.name}</h2>
            <p className="text-sm text-gray-500">{template.description}</p>
          </div>
          <button onClick={() => setSelectedTemplate(null)} className="text-gray-400 hover:text-gray-600">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {/* Workflow Info */}
          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-xs text-gray-500 mb-1">Estimated Duration</p>
              <p className="font-medium">{template.estimatedDuration}</p>
            </div>
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-xs text-gray-500 mb-1">Change Window</p>
              <p className="font-medium">{template.changeWindow}</p>
            </div>
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-xs text-gray-500 mb-1">Approval Required</p>
              <p className="font-medium">{template.approvalRequired ? 'Yes' : 'No'}</p>
            </div>
          </div>

          {/* Steps */}
          <h3 className="font-semibold mb-3">Workflow Steps</h3>
          <div className="space-y-2 mb-6">
            {template.steps.map((step, idx) => (
              <div key={step.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <div className="w-6 h-6 rounded-full bg-[#4a70ff] text-white flex items-center justify-center text-xs font-medium">
                  {step.id}
                </div>
                <div className="flex-1">
                  <p className="font-medium text-sm">{step.name}</p>
                  <p className="text-xs text-gray-500">Connector: {step.connector}</p>
                </div>
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <RotateCcw className="w-3 h-3" />
                  Retry: {step.retry}
                </div>
                {step.compensation && (
                  <div className="text-xs text-amber-600">Rollback: {step.compensation}</div>
                )}
              </div>
            ))}
          </div>

          {/* Features */}
          <h3 className="font-semibold mb-3">Safety Features</h3>
          <div className="grid grid-cols-2 gap-3 mb-6">
            {[
              { label: 'Idempotency Keys', desc: 'Prevent duplicate executions' },
              { label: 'Checkpoints', desc: 'Resume from failure point' },
              { label: 'Compensations', desc: 'Automatic rollback on failure' },
              { label: 'Dry Run Mode', desc: 'Test without side effects' }
            ].map((feature, idx) => (
              <div key={idx} className="flex items-center gap-2 p-3 border border-gray-200 rounded-lg">
                <CheckCircle2 className="w-4 h-4 text-primary" />
                <div>
                  <p className="text-sm font-medium">{feature.label}</p>
                  <p className="text-xs text-gray-500">{feature.desc}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Approvers */}
          {template.approvalRequired && (
            <>
              <h3 className="font-semibold mb-3">Required Approvers</h3>
              <div className="flex gap-2 mb-6">
                {template.approvers.map((approver, idx) => (
                  <span key={idx} className="px-3 py-1.5 bg-primary/10 text-primary rounded-lg text-sm">
                    {approver}
                  </span>
                ))}
              </div>
            </>
          )}
        </div>

        <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex gap-2">
          <button onClick={() => setSelectedTemplate(null)} className="px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-100">
            Close
          </button>
          <button onClick={() => handleAction(`Start ${template.name} (Dry Run)`)} className="px-4 py-2 border border-[#4a70ff] text-[#4a70ff] rounded-lg hover:bg-[#4a70ff]/5">
            Start Dry Run
          </button>
          <button onClick={() => handleAction(`Start ${template.name}`)} className="px-4 py-2 bg-[#4a70ff] text-white rounded-lg hover:bg-[#3a5fe6]">
            Start Workflow
          </button>
        </div>
      </div>
    </div>
  );

  const renderRunDetail = (run) => (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-xl shadow-lg border border-gray-200 w-full max-w-3xl max-h-[90vh] overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <div className="flex items-center gap-3">
              <h2 className="text-lg font-semibold">{run.id}</h2>
              <span className={`px-2 py-0.5 rounded text-xs font-medium ${statusColors[run.status]?.bg} ${statusColors[run.status]?.text}`}>
                {run.status.replace('_', ' ')}
              </span>
            </div>
            <p className="text-sm text-gray-500">{run.workflowName} - {run.target}</p>
          </div>
          <button onClick={() => setSelectedRun(null)} className="text-gray-400 hover:text-gray-600">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {/* Run Info */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="p-4 bg-gray-50 rounded-lg">
              <p className="text-xs text-gray-500 mb-1">Started</p>
              <p className="font-medium">{new Date(run.startedAt).toLocaleString()}</p>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg">
              <p className="text-xs text-gray-500 mb-1">Duration</p>
              <p className="font-medium">{run.duration}</p>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg">
              <p className="text-xs text-gray-500 mb-1">Initiated By</p>
              <p className="font-medium">{run.initiatedBy}</p>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg">
              <p className="text-xs text-gray-500 mb-1">Idempotency Key</p>
              <code className="text-xs bg-gray-200 px-2 py-0.5 rounded">{run.idempotencyKey}</code>
            </div>
          </div>

          {/* Progress */}
          <h3 className="font-semibold mb-3">Progress</h3>
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-600">Steps completed</span>
              <span className="font-medium">{run.stepsCompleted} / {run.stepsTotal}</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-[#4a70ff] h-2 rounded-full transition-all" 
                style={{ width: `${(run.stepsCompleted / run.stepsTotal) * 100}%` }}
              />
            </div>
          </div>

          {/* Approvals */}
          {run.approvedBy.length > 0 && (
            <>
              <h3 className="font-semibold mb-3">Approvals</h3>
              <div className="flex flex-wrap gap-2 mb-6">
                {run.approvedBy.map((approver, idx) => (
                  <div key={idx} className="flex items-center gap-2 px-3 py-1.5 bg-primary/10 text-primary rounded-lg text-sm">
                    <CheckCircle2 className="w-4 h-4" />
                    {approver}
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Audit Events */}
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold">Audit Events</h3>
            <span className="text-sm text-gray-500">{run.auditEvents} events</span>
          </div>

          {/* Evidence Bundle */}
          {run.evidenceBundleId && (
            <div className="p-4 bg-primary/10 border border-primary/20 rounded-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <FileText className="w-5 h-5 text-primary" />
                  <div>
                    <p className="font-medium text-primary">Evidence Bundle Available</p>
                    <p className="text-sm text-primary/80">{run.evidenceBundleId}</p>
                  </div>
                </div>
                <button
                  onClick={() => handleAction(`View evidence bundle ${run.evidenceBundleId}`)}
                  className="px-3 py-1.5 bg-primary text-white rounded-lg text-sm hover:bg-primary/90"
                >
                  View Bundle
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex gap-2">
          <button onClick={() => setSelectedRun(null)} className="px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-100">
            Close
          </button>
          {run.status === 'in_progress' && (
            <button onClick={() => handleAction('Pause workflow')} className="px-4 py-2 border border-slate-400 text-slate-600 rounded-lg hover:bg-slate-50">
              Pause
            </button>
          )}
          {run.status === 'pending_approval' && (
            <button onClick={() => handleAction('Approve workflow')} className="px-4 py-2 bg-[#4a70ff] text-white rounded-lg hover:bg-[#3a5fe6]">
              Approve
            </button>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-[1400px] mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-black mb-1">Automation</h1>
          <p className="text-gray-500">Safe, auditable workflows for IT operations</p>
        </div>

        {selectedTemplate && renderWorkflowDetail(selectedTemplate)}
        {selectedRun && renderRunDetail(selectedRun)}

        {/* Stats */}
        <div className="grid grid-cols-4 gap-4 mb-8">
          {[
            { label: 'Workflow Templates', value: '5', icon: FileText, color: 'primary' },
            { label: 'Runs This Week', value: '23', icon: Play, color: 'primary' },
            { label: 'Pending Approval', value: '1', icon: Clock, color: 'slate' },
            { label: 'Success Rate', value: '98%', icon: CheckCircle2, color: 'primary' }
          ].map((stat, idx) => (
            <div key={idx} className="bg-white rounded-xl border border-gray-200 p-5">
              <div className="flex items-center justify-between mb-3">
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${
                  stat.color === 'primary' ? 'bg-primary/10' :
                  stat.color === 'slate' ? 'bg-slate-200' : 'bg-gray-100'
                }`}>
                  <stat.icon className={`w-4.5 h-4.5 ${
                    stat.color === 'primary' ? 'text-primary' :
                    stat.color === 'slate' ? 'text-slate-700' : 'text-gray-600'
                  }`} />
                </div>
              </div>
              <p className="text-2xl font-bold text-black">{stat.value}</p>
              <p className="text-sm text-gray-500">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Workflow Templates */}
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-black mb-4">Workflow Templates</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {workflowTemplates.map((template) => (
              <div 
                key={template.id}
                className="bg-white rounded-xl border border-gray-200 p-5 hover:border-[#4a70ff]/30 hover:shadow-lg hover:shadow-[#4a70ff]/5 transition-all cursor-pointer"
                onClick={() => setSelectedTemplate(template)}
              >
                <div className="flex items-center justify-between mb-3">
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                    template.category === 'Identity' ? 'bg-primary/10 text-primary' :
                    template.category === 'Security' ? 'bg-slate-900 text-white' :
                    'bg-primary/10 text-primary'
                  }`}>{template.category}</span>
                  <span className="text-xs text-gray-500">{template.steps.length} steps</span>
                </div>
                <h3 className="font-semibold text-black mb-1">{template.name}</h3>
                <p className="text-sm text-gray-500 mb-4">{template.description}</p>
                <div className="flex items-center justify-between text-xs text-gray-500">
                  <span className="flex items-center gap-1">
                    <Clock className="w-3.5 h-3.5" />
                    {template.estimatedDuration}
                  </span>
                  {template.approvalRequired && (
                    <span className="flex items-center gap-1">
                      <Users className="w-3.5 h-3.5" />
                      Approval required
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Workflow Run History */}
        <div>
          <h2 className="text-lg font-semibold text-black mb-4">Run History</h2>
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Run ID</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Workflow</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Target</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Status</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Duration</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Initiated By</th>
                  <th className="px-5 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {workflowRuns.map((run) => {
                  const StatusIcon = statusColors[run.status]?.icon || Clock;
                  return (
                    <tr key={run.id} className="hover:bg-gray-50 transition-colors cursor-pointer" onClick={() => setSelectedRun(run)}>
                      <td className="px-5 py-4">
                        <span className="font-medium text-[#4a70ff]">{run.id}</span>
                        {run.dryRun && <span className="ml-2 px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded text-xs">Dry Run</span>}
                      </td>
                      <td className="px-5 py-4 text-sm text-gray-600">{run.workflowName}</td>
                      <td className="px-5 py-4 text-sm text-gray-600">{run.target}</td>
                      <td className="px-5 py-4">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${statusColors[run.status]?.bg} ${statusColors[run.status]?.text}`}>
                          <StatusIcon className="w-3 h-3" />
                          {run.status.replace('_', ' ')}
                        </span>
                      </td>
                      <td className="px-5 py-4 text-sm text-gray-600">{run.duration}</td>
                      <td className="px-5 py-4 text-sm text-gray-600">{run.initiatedBy}</td>
                      <td className="px-5 py-4">
                        <ChevronRight className="w-5 h-5 text-gray-300" />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};